import { useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Loader2, RotateCcw, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useTotalCliUsage } from '../api';
import type { TotalCliUsageRow } from '../types';
import { ensureArray } from '@/lib/utils/ensure-array';

function fmt(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function CliUsagePage() {
  const today = new Date();
  // Live picker values — free to change without triggering a fetch.
  const [startDate, setStartDate] = useState<Date>(today);
  const [endDate, setEndDate] = useState<Date>(today);
  const mutation = useTotalCliUsage();
  const rows = ensureArray<TotalCliUsageRow>(mutation.data ?? []);

  const runSearch = (start: Date, end: Date) => {
    mutation.mutate({ startDate: fmt(start), endDate: fmt(end) });
  };

  // Load today's usage automatically on first visit — no need to click
  // Search just to see the default (current date) view.
  useEffect(() => {
    runSearch(today, today);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => runSearch(startDate, endDate);
  const handleReset = () => {
    const t = new Date();
    setStartDate(t);
    setEndDate(t);
    runSearch(t, t);
  };

 const columns = useMemo<ColumnDef<TotalCliUsageRow>[]>(
  () => [
    { accessorKey: 'id', header: 'ID', size: 80 },
    { accessorKey: 'cli', header: 'CLI', size: 160 },
    { accessorKey: 'requestDate', header: 'Request Date', size: 140 },
    { accessorKey: 'totalNos', header: 'Total Nos', size: 120 },
    { accessorKey: 'nosProcessed', header: 'Processed', size: 120 },
    { accessorKey: 'dndCount', header: 'DND Count', size: 120 },
    {
      id: 'pending',
      header: 'Pending',
      size: 120,
      cell: ({ row }) => {
        const total = Number(row.original.totalNos ?? 0);
        const processed = Number(row.original.nosProcessed ?? 0);
        const dnd = Number(row.original.dndCount ?? 0);
        return total - processed - dnd;
      },
    },
    { accessorKey: 'connectedCalls', header: 'Connected', size: 120 },
  ],
  [],
);

  return (
    <FillPage>
      <PageHeader
        title="CLI Usage"
        description="View CLI-wise call usage statistics for a date range."
      />

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Filter</CardTitle>
          <CardDescription>Narrow usage by date range.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-44">
              <label className="mb-1 block text-sm font-medium">Start Date</label>
              <DatePicker
                value={startDate}
                onChange={(d) => d && setStartDate(d)}
                toDate={new Date()}
              />
            </div>
            <div className="w-44">
              <label className="mb-1 block text-sm font-medium">End Date</label>
              <DatePicker
                value={endDate}
                onChange={(d) => d && setEndDate(d)}
                fromDate={startDate}
                toDate={new Date()}
              />
            </div>

            <Button onClick={handleSearch} disabled={mutation.isPending}>
              {mutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              Search
            </Button>
            <Button variant="outline" onClick={handleReset} disabled={mutation.isPending}>
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <DataTable
  columns={columns}
  data={rows}
  tableId="cli:usage:v1"
  searchPlaceholder="Search CLI..."
  isLoading={mutation.isPending}
  emptyTitle="No records"
  emptyDescription="No CLI usage data found for the selected range."
/>
    </FillPage>
  );
}